import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import SimpleDialog from "components/SimpleDialog/SimpleDialog";
import AmexBbmslForm from "./AmexBbmslForm";
import styles from "./AmexSponsoredMerchantDialog.module.scss";
import { saveAmexSponsoredMerchant } from "../../model.jsx";
import useSnackbarMAS from "utils/useSnackbarMAS/useSnackbarMAS";

const PAGE_LOCALE_KEY = "page.merchants.merchantDetail.";

/**
 * Reusable AMEX Sponsored Merchant Dialog Component
 * Handles the complete flow of checking, displaying, and managing AMEX sponsored merchant data
 */
/**
 * @param {Object} props
 * @param {import("utils/usePageWrapper/usePageWrapper").CallApisFn<import("../../OfflineMerchantDetail").offlineMerchantDetailPartitionKeyType>} props.callApis 
 * @returns 
 */
const AmexSponsoredMerchantDialog = ({
  callApis,
  isOpen,
  onClose,
  merchantId,
  merchant,
  mccOptions,
  updateGsiState,
  isLoading,
  amexSponsoredMerchant,
}) => {
  const { t } = useTranslation();
  const { snackbarError, snackbarSuccess } = useSnackbarMAS();
  // Initialize form with merchant data
  const defaultValues = {
    owners: [{
      country: "",
      region: "",
    }],
    merchantName: merchant?.ownerName,
    businessName: merchant?.businessName || merchant?.storeName,
    // Ensure matches Selection option value type
    mccCode: merchant?.merchantCategoryCode
      ? String(merchant?.merchantCategoryCode)
      : "",
    merchantRegNo: merchant?.businessRegistration,
    contactPhone: merchant?.phone,
    email: merchant?.email,
    merchantAddress: merchant?.ownerAddress1,
    signerCountry: "",
    signerRegion: "",
  }
  const formProps = useForm({
    defaultValues,
  });

  // UI state only (data/loading come from parent)
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const saveAmexSponsoredMerchantData = useCallback(
    async (formData) => {
      if (!merchantId) return { success: false };
      try {
        setIsSubmitting(true);
        const payload = { ...amexSponsoredMerchant, ...formData, merchantId };
        // Normalize fields to API expectations
        // signerCountry should be country code only (e.g., "AF")
        if (payload.signerCountry) {
          payload.signerCountry = String(payload.signerCountry).split(":")[0];
        }
        // marketingIndicator should be boolean
        if (payload.marketingIndicator !== undefined) {
          payload.marketingIndicator =
            payload.marketingIndicator === true ||
            payload.marketingIndicator === "true";
        }
        // API expects significantOwner as a JSON string, not raw owners array
        if (Array.isArray(formData?.owners)) {
          payload.significantOwner = JSON.stringify(formData.owners);
        }
        delete payload.owners;
        const [saveResp] = await callApis([{
          api: saveAmexSponsoredMerchant,
          payload: payload,
          blockOnError: false,
          blockOnUnauthorized: false,
        }])
        if (saveResp.isError)
          throw new Error(saveResp?.response?.message || "save failed");
        return { success: true };
      } catch (e) {
        console.error(e);
        return { success: false, error: e.message };
      } finally {
        setIsSubmitting(false);
      }
    },
    [merchantId, amexSponsoredMerchant],
  );

  const resetState = useCallback(() => {
    setIsSubmitting(false);
    setIsClosing(false);
    // Reset form to default values when closing
    formProps.reset(defaultValues);
  }, [formProps, defaultValues]);

  // Handle dialog close
  const handleClose = useCallback((result) => {
    resetState();
    onClose && onClose(result);
  }, [resetState, onClose]);

  // Handle form submission
  const handleFormSubmit = useCallback(
    async (formData) => {
      try {
        setIsSubmitting(true);
        const resp = await saveAmexSponsoredMerchantData(formData);
        console.log("resp", resp);
        if (resp.success) {
          snackbarSuccess({
            message: t(
              `${PAGE_LOCALE_KEY}processorMdr.amexForm.success.saveSuccess`,
            ),
          });
          resetState();
          onClose && onClose({ submitted: true });
        } else {
          console.error("Error in form submission:", resp.error);
          snackbarError({
            message: t(
              `${PAGE_LOCALE_KEY}processorMdr.amexForm.error.saveFailed`,
            ),
          });
        }
      } catch (error) {
        console.error("Error in form submission:", error);
      } finally {
        setIsSubmitting(false);
      }
    },
    [saveAmexSponsoredMerchantData, resetState, onClose, t, snackbarSuccess, snackbarError],
  );

  // Render loading state
  const renderLoadingContent = () => (
    <div className={`${styles.loadingContainer} ${styles.contentBase}`}>
      <div className={styles.loadingTitle}>
        {t(`${PAGE_LOCALE_KEY}processorMdr.amexForm.checkingData`)}
      </div>
      <div className={styles.loadingSubtitle}>
        {t(`${PAGE_LOCALE_KEY}processorMdr.amexForm.pleaseWait`)}
      </div>
    </div>
  );

  // Render form content
  const renderFormContent = () => (
    <AmexBbmslForm
      formProps={formProps}
      mccOptions={mccOptions}
      updateGsiState={updateGsiState}
    />
  );

  // Render dialog content based on state
  const renderDialogContent = () => {
    if (isLoading || amexSponsoredMerchant) return renderLoadingContent();
    return renderFormContent();
  };

  // Use a smaller dialog width while fetching to reduce visual footprint
  const dialogMaxWidth = isLoading || amexSponsoredMerchant ? "sm" : "lg";

  return (
    <SimpleDialog
      key={t(`${PAGE_LOCALE_KEY}processorMdr.amexForm.title`)}
      open={isOpen}
      dialogOncloseFn={() => handleClose({ submitted: false })}
      title={t(`${PAGE_LOCALE_KEY}processorMdr.amexForm.title`)}
      content={renderDialogContent()}
      confirmFn={formProps.handleSubmit(handleFormSubmit)}
      positiveAction={t(`${PAGE_LOCALE_KEY}processorMdr.amexForm.button.save`)}
      positiveActionLoading={isSubmitting || isLoading}
      positiveActionDisabled={
        isSubmitting || isLoading || isClosing || !!amexSponsoredMerchant
      }
      maxWidth={dialogMaxWidth}
    />
  );
};

export default React.memo(AmexSponsoredMerchantDialog);
